// src/views/AddStockSales.js
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import Breadcrumb from "../components/Breadcrumb";
import ProjectService from "../services/ProjectService";
import ProjectStoreService from "../services/ProjectStoreService";
import StockSalesService from "../services/StockSalesService";
import StorageService from "../services/StorageService";
import TransporterService from "../services/TransporterService";
import ProjectItemService from "../services/ProjectItemService";
import Select from "react-select";
import ProjectTeamService from "../services/ProjectTeamService";
import Loading from "../components/loading";
import MessageDialog from "../components/MessageDialog";
import usePermission from "../utils/UsePermission";
import ScreenData from "../config/ScreenData";
import UnAuthorized from "../views/Unathorized";
import CustomerService from "../services/CustomerServices";
import CustomerStoreService from "../services/CustomerStoreService";

export default function AddStockSales() {
  const { id } = useParams(); // id for edit
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [stores, setStores] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerStores, setCustomerStores] = useState([]);
  const [stockItems, setStockItems] = useState([
    {
      itemCategoryId: "",
      itemId: "",
      itemDescription: "",
      uomId: "",
      quantity: "",
    },
  ]);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [projectEmployees, setProjectEmployees] = useState([]);
  const [messageType, setMessageType] = useState("error"); // error | success

  const companyId = StorageService.activeCompany()?.companyId ?? null;
  const employeeId = StorageService.loginData()?.employeeId ?? "System";

  const [projectFullData, setProjectFullData] = useState([]);
  const [categories, setCategories] = useState([]);

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMessageType(type);
  };

  const { permission } = usePermission(
    ScreenData.Stock_Out,
    showMessage
  );

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm({ mode: "onChange", reValidateMode: "onChange" });

  const storeIdValue = watch("storeId");
  const selectedStore = stores.find((s) => String(s.storeId ?? s.id) === String(storeIdValue));
  const storeName = selectedStore ? selectedStore.storeName || selectedStore.name || "" : "";

  const getFilteredCategories = () => {
    if (!storeIdValue || !storeName) return categories;

    const storeNameLower = storeName.trim().toLowerCase();

    if (storeNameLower.startsWith("reuse")) {
      return categories.filter((c) =>
        c.itemCategoryName && c.itemCategoryName.trim().toLowerCase().startsWith("r")
      );
    }

    if (storeNameLower.startsWith("scrap") || storeNameLower.startsWith("s")) {
      return categories.filter((c) =>
        c.itemCategoryName && c.itemCategoryName.trim().toLowerCase().startsWith("s")
      );
    }

    return categories.filter((c) => {
      if (!c.itemCategoryName) return false;
      const firstChar = c.itemCategoryName.trim().toLowerCase().charAt(0);
      return firstChar !== "r" && firstChar !== "s";
    });
  };

  const loadProjectEmployees = async (projectId) => {
    if (!projectId) {
      setProjectEmployees([]);
      return;
    }
    try {
      const res = await ProjectTeamService.getByProjectId(projectId);
      const seen = new Set();
      const mapped = [];
      (res || []).forEach((e) => {
        const empId = e.employeeId;
        const fullName = `${e.firstName || ""} ${e.lastName || ""}`.trim();
        const key = empId ? String(empId) : fullName;
        if (key && !seen.has(key)) {
          seen.add(key);
          mapped.push({
            id: empId,
            name: fullName,
          });
        }
      });
      setProjectEmployees(mapped);
    } catch (err) {
      console.error("Project employees load error", err);
      setProjectEmployees([]);
    }
  };

  const loadProjects = async () => {
    const emp = StorageService.loginData();
    const comp = StorageService.activeCompany();

    try {
      let projectsData = [];
      if (
        emp.roleName === "Super Admin" ||
        emp.roleName === "SuperAdmin" ||
        emp.roleName === "Data Admin" ||
        emp.roleName === "DataAdmin" ||
        Number(emp.roleId) === 1 ||
        Number(emp.roleId) === 2
      ) {
        const res = await ProjectService.getActive(comp.companyId);
        projectsData = (res || []).map(p => ({
          projectId: p.id,
          projectName: p.projectName,
          projectCode: p.projectCode
        }));
      } else {
        const res = await ProjectService.getAssignedProjects(
          comp.companyId,
          emp.employeeId
        );
        projectsData = (res?.data?.data || []).map(p => ({
          projectId: p.projectId,
          projectName: p.projectName,
          projectCode: p.projectCode
        }));
      }
      setProjects(projectsData);
    } catch (err) {
      console.error("Error loading projects:", err);
      setProjects([]);
    }
  };

  const loadProjectFullData = async (projectId) => {
    try {
      const res = await ProjectItemService.getProjectFullData(projectId);
      const data = res?.data?.data || [];
      setProjectFullData(data);
      setCategories(data);
      return data;
    } catch (err) {
      console.error("Category load error", err);
      setProjectFullData([]);
      setCategories([]);
      return [];
    }
  };

  const handleCategoryChange = (index, categoryId) => {
    const updated = [...stockItems];
    updated[index].itemCategoryId = categoryId;
    updated[index].itemId = "";
    updated[index].itemDescription = "";
    updated[index].uomId = "";
    setStockItems(updated);
  };

  const getItemOptions = (categoryId) => {
    const cat = projectFullData.find(
      (c) => String(c.itemCategoryId) === String(categoryId)
    );
    if (!cat) return [];
    return cat.items.map((i) => ({
      value: i.itemId,
      label: i.itemCode,
    }));
  };

  const loadStores = async (projectId) => {
    if (!projectId) {
      setStores([]);
      return;
    }
    try {
      const res = await ProjectStoreService.getProjectStoreById(projectId);
      const list = res.data || res || [];
      setStores(
        list.filter((s) => String(s.projectId) === String(projectId))
      );
    } catch (err) {
      console.error("Store load error", err);
      setStores([]);
    }
  };

  const loadTransporters = async () => {
    try {
      const res = await TransporterService.getActiveTransporters(1, 100);
      setTransporters(res?.data || []);
    } catch (err) {
      console.error("Transporter load error:", err);
      setTransporters([]);
    }
  };

  const loadCustomers = async () => {
    try {
      if (companyId) {
        const res = await CustomerService.getByCompanyId(companyId);
        const list = res?.data || res || [];
        setCustomers(list.filter((c) => c.status === "Active"));
      }
    } catch (err) {
      console.error("Error loading customers:", err);
      setCustomers([]);
    }
  };

  const loadCustomerStores = async (customerId) => {
    if (!customerId) {
      setCustomerStores([]);
      return;
    }
    try {
      const res = await CustomerStoreService.getByCustomer(customerId);
      const list = res?.data || res || [];
      setCustomerStores(list.filter((cs) => cs.status === "Active"));
    } catch (err) {
      console.error("Error loading customer stores:", err);
      setCustomerStores([]);
    }
  };

  const loadItemsByProject = async (projectId) => {
    if (!projectId) {
      setItems([]);
      return;
    }
    try {
      const res = await ProjectItemService.getByProjectId(projectId);
      const list = res?.data?.data || [];
      const mapped = list.map((x) => ({
        id: x.itemId,
        itemId: x.itemId,
        itemName: x.itemCode,
        itemDescription: x.itemDescription,
        uomName: x.uomName,
      }));
      setItems(mapped);
    } catch (err) {
      console.error("Project item load error", err);
      setItems([]);
    }
  };

  const fetchInvoiceNo = async (projectId, storeId) => {
    if (!projectId || !storeId) return;
    try {
      const res = await StockSalesService.previewInvoiceNo(projectId, storeId);
      setValue("invoiceNo", res?.data?.invoiceNo || "");
    } catch (err) {
      console.error("StockSales invoice fetch error", err);
      setValue("invoiceNo", "");
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadProjects();
      await loadTransporters();
      await loadCustomers();

      if (isEdit) await loadForEdit(id);
      setLoading(false);
    };

    init();
  }, [id]);

  // Auto-select dropdowns if only 1 option is available
  useEffect(() => {
    if (projects.length === 1 && !isEdit) {
      const singleProj = projects[0];
      const pid = String(singleProj.projectId ?? singleProj.id);
      setValue("projectId", pid);
      
      // Inline Project onChange logic:
      setValue("storeId", "");
      setValue("invoiceNo", "");
      setValue("receiverName", "");
      setItems([]);
      setStockItems([
        { itemId: "", itemDescription: "", uomId: "", quantity: "" }
      ]);
      loadStores(pid);
      loadItemsByProject(pid);
      loadProjectFullData(pid);
      loadProjectEmployees(pid);
    }
  }, [projects, isEdit]);

  useEffect(() => {
    const filteredStores = stores.filter((s) => {
      const name = (s.storeName || s.name || "").trim().toLowerCase();
      return !name.startsWith("scrap") && !name.startsWith("s");
    });
    if (filteredStores.length === 1 && !isEdit) {
      const singleStore = filteredStores[0];
      const sid = String(singleStore.storeId ?? singleStore.id);
      setValue("storeId", sid);
      
      // Inline Store onChange logic:
      const pid = getValues("projectId");
      if (pid && sid) {
        fetchInvoiceNo(pid, sid);
      }
    }
  }, [stores, isEdit]);

  useEffect(() => {
    if (customers.length === 1 && !isEdit) {
      const singleCust = customers[0];
      const cid = String(singleCust.id);
      setValue("customerId", cid);
      
      // Inline Customer onChange logic:
      setValue("customerStoreId", "");
      loadCustomerStores(cid);
    }
  }, [customers, isEdit]);

  useEffect(() => {
    if (customerStores.length === 1 && !isEdit) {
      const singleStore = customerStores[0];
      const csId = String(singleStore.id);
      setValue("customerStoreId", csId);
    }
  }, [customerStores, isEdit]);

  useEffect(() => {
    if (transporters.length === 1 && !isEdit) {
      const singleTrans = transporters[0];
      const tId = String(singleTrans.id ?? singleTrans.transporterId);
      setValue("transporterId", tId);
    }
  }, [transporters, isEdit]);

  const loadForEdit = async (editId) => {
    try {
      const res = await StockSalesService.getById(editId);
      const sale = res?.data?.data;
      if (!sale) return;

      setValue("projectId", String(sale.projectId ?? ""));
      await loadProjectEmployees(sale.projectId);
      const projectData = await loadProjectFullData(sale.projectId);
      await loadItemsByProject(sale.projectId);
      await loadStores(sale.projectId);

      setValue("storeId", String(sale.storeId ?? ""));
      if (sale.customerId) {
        await loadCustomerStores(sale.customerId);
      }
      setValue("customerId", sale.customerId ? String(sale.customerId) : "");
      setValue("customerStoreId", sale.customerStoreId ? String(sale.customerStoreId) : "");
      setValue("invoiceNo", sale.invoiceNo ?? "");
      setValue("transporterId", String(sale.transporterId ?? ""));
      setValue("vehicleNo", sale.vehicleNo ?? "");
      setValue("saleDate", sale.saleDate ? sale.saleDate.split("T")[0] : "");
      setValue("invoiceDate", sale.invoiceDate ? sale.invoiceDate.split("T")[0] : "");
      setValue("stockSalesNotes", sale.stockSalesNotes ?? "");

      const mappedItems = sale.details?.length
        ? sale.details.map((d) => {
            const category = projectData.find(cat =>
              cat.items?.some(i => String(i.itemId) === String(d.itemId))
            );

            return {
              itemCategoryId: category ? String(category.itemCategoryId) : "",
              itemId: String(d.itemId ?? ""),
              itemDescription: d.itemCode ?? "",
              uomId: d.UOM ?? "",
              quantity: String(d.quantity ?? ""),
              detailId: d.detailId
            };
          })
        : [{
            itemCategoryId: "",
            itemId: "",
            itemDescription: "",
            uomId: "",
            quantity: ""
          }];

      setStockItems(mappedItems);
    } catch (err) {
      console.error("Edit load error", err);
      showMessage("Failed to load data for modify", "error");
    }
  };

  const handleAddRow = () =>
    setStockItems([...stockItems, {
      itemCategoryId: "",
      itemId: "",
      itemDescription: "",
      uomId: "",
      quantity: "",
    }]);

  const handleDeleteRow = (idx) => {
    const copy = [...stockItems];
    copy.splice(idx, 1);
    setStockItems(copy);
  };

  const handleChange = async (idx, field, value) => {
    if (
      field === "itemId" &&
      value &&
      stockItems.some(
        (row, rowIndex) => rowIndex !== idx && String(row.itemId) === String(value)
      )
    ) {
      showMessage("This item is already selected in another row.", "error");
      return;
    }

    const copy = [...stockItems];

    if (field === "quantity") {
      const reqQty = Number(value);
      if (isNaN(reqQty)) return;
      if (reqQty <= 0) {
        showMessage("Quantity must be greater than 0", "error");
        return;
      }
    }

    copy[idx][field] = value;

    if (field === "itemId") {
      const selectedItem =
        items.find((it) => String(it.id) === String(value)) ||
        items.find((it) => String(it.itemId) === String(value));

      copy[idx].uomId = selectedItem?.uomName || "";
      copy[idx].itemDescription = selectedItem?.itemDescription || "";
      copy[idx].quantity = "";
    }

    setStockItems(copy);
  };

  const handleQuantityChange = (index, e) => {
    let value = e.target.value;
    if (value !== "" && !/^\d*\.?\d{0,3}$/.test(value)) return;

    setStockItems(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        quantity: value
      };
      return updated;
    });
  };

  const handleQuantityBlur = (index) => {
    setStockItems(prev => {
      const updated = [...prev];
      const val = updated[index].quantity;
      if (val !== "" && !isNaN(Number(val))) {
        updated[index].quantity = Number(val).toFixed(3);
      }
      return updated;
    });
  };

  const onSubmit = async (form) => {
    if (!stockItems || stockItems.length === 0) {
      showMessage("Add at least one item row", "error");
      return;
    }
    const bad = stockItems.some((r) => !r.itemId || !r.uomId || !r.quantity);
    if (bad) {
      showMessage("All detail rows must have Item, UOM and Quantity", "error");
      return;
    }

    const duplicateItemIds = stockItems
      .map((r) => String(r.itemId).trim())
      .filter(Boolean);

    if (duplicateItemIds.length !== new Set(duplicateItemIds).size) {
      showMessage("Duplicate items are not allowed in details.", "error");
      return;
    }

    const today = new Date().toLocaleDateString("en-CA");

    if (form.saleDate > today) {
      showMessage("Sale Date cannot be a future date", "error");
      return;
    }

    const invalidQty = stockItems.some(
      (r) => Number(r.quantity) <= 0
    );

    if (invalidQty) {
      showMessage(
        "Quantity must be greater than 0 for all items",
        "error"
      );
      return;
    }

    setLoading(true);
    try {
      const details = stockItems.map((r) => ({
        itemId: Number(r.itemId),
        UOM: r.uomId,
        quantity: Number(r.quantity),
        status: "Active",
        createdBy: employeeId,
        createdDate: new Date().toISOString(),
        updatedBy: employeeId,
        updatedDate: new Date().toISOString(),
      }));

      const payload = {
        companyId,
        projectId: Number(form.projectId),
        storeId: Number(form.storeId),
        customerId: Number(form.customerId),
        customerStoreId: Number(form.customerStoreId),
        invoiceNo: form.invoiceNo,
        invoiceDate: form.invoiceDate,
        transporterId: Number(form.transporterId),
        vehicleNo: form.vehicleNo ? form.vehicleNo.trim() : null,
        saleDate: form.saleDate,
        stockSalesNotes: form.stockSalesNotes,
        status: "pending",
        roleId: Number(StorageService.loginData()?.roleId),
        createdBy: employeeId,
        updatedBy: employeeId,
        createdDate: new Date().toISOString(),
        updatedDate: new Date().toISOString(),
        stockSalesDetails: details,
      };

      if (isEdit) {
        await StockSalesService.update(id, payload);
        showMessage("Stock Sales updated successfully", "success");
      } else {
        await StockSalesService.save(payload);
        showMessage("Stock Sales saved successfully", "success");
      }

      setTimeout(() => navigate("/cpnl/stocksales/view"), 600);
    } catch (err) {
      console.error("Save error", err);
      showMessage(
        err?.response?.data?.message || err?.message || "Error saving Stock Sales",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  if (permission === null) {
    return (
      <div className="text-center py-5">
        <Loading />
      </div>
    );
  }

  if (!permission || permission.canView !== 1) {
    return <UnAuthorized />;
  }

  return (
    <>
      <Breadcrumb
        pageTitle={isEdit ? "Modify Stock Sales" : "Add Stock Sales"}
        secondTitle="Inventory"
      />

      <div className="card p-4 bg-white shadow-sm">
        {loading ? (
          <div className="text-center py-5">
            <Loading />
          </div>
        ) : (
          <>
            <MessageDialog
              message={message}
              type={messageType}
              setMessage={setMessage}
              setMsgType={setMessageType}
            />

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="row">
                <div className="mb-3 col-md-4">
                  <label className="form-label">Project<span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    {...register("projectId", { required: "Project is required" })}
                    onChange={(e) => {
                      const pid = e.target.value;
                      setValue("projectId", pid);
                      setValue("storeId", "");
                      setValue("invoiceNo", "");
                      setValue("receiverName", "");
                      setItems([]);
                      setStockItems([
                        { itemId: "", itemDescription: "", uomId: "", quantity: "" }
                      ]);
                      loadStores(pid);
                      loadItemsByProject(pid);
                      loadProjectFullData(pid);
                      loadProjectEmployees(pid);
                    }}
                  >
                    <option value="">Select Project</option>
                    {projects.map((p) => (
                      <option key={p.projectId} value={p.projectId}>
                        {p.projectCategoryName ? `${String(p.projectCategoryName).trim().charAt(0).toUpperCase()}*${p.projectName}` : (p.projectCode ? `${String(p.projectCode).trim().charAt(0).toUpperCase()}*${p.projectName}` : p.projectName)}
                      </option>
                    ))}
                  </select>
                  {errors.projectId && (
                    <small className="text-danger">{errors.projectId.message}</small>
                  )}
                </div>

                <div className="mb-3 col-md-4">
                  <label className="form-label">
                    From Store <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    {...register("storeId", { required: "From Store is required" })}
                    onChange={(e) => {
                      const sid = e.target.value;
                      setValue("storeId", sid);
                      const pid = getValues("projectId");
                      if (pid && sid) {
                        fetchInvoiceNo(pid, sid);
                      }
                    }}
                  >
                    <option value="">Select From Store</option>
                    {stores
                      .filter((s) => {
                        const name = (s.storeName || s.name || "").trim().toLowerCase();
                        return !name.startsWith("scrap") && !name.startsWith("s");
                      })
                      .map((s) => (
                        <option key={s.id ?? s.storeId} value={s.storeId ?? s.id}>
                          {s.storeName || s.name}
                        </option>
                      ))}
                  </select>
                  {errors.storeId && (
                    <small className="text-danger">{errors.storeId.message}</small>
                  )}
                </div>

                <div className="mb-3 col-md-4">
                  <label className="form-label">Customer<span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    {...register("customerId", { required: "Customer is required" })}
                    onChange={(e) => {
                      const cid = e.target.value;
                      setValue("customerId", cid);
                      setValue("customerStoreId", "");
                      loadCustomerStores(cid);
                    }}
                  >
                    <option value="">Select Customer</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.customerName} ({c.customerCode})
                      </option>
                    ))}
                  </select>
                  {errors.customerId && (
                    <small className="text-danger">{errors.customerId.message}</small>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-4">
                  <label className="form-label">Customer Store<span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    {...register("customerStoreId", { required: "Customer Store is required" })}
                  >
                    <option value="">Select Customer Store</option>
                    {customerStores.map((cs) => (
                      <option key={cs.id} value={cs.id}>
                        {cs.customerStoreName}
                      </option>
                    ))}
                  </select>
                  {errors.customerStoreId && (
                    <small className="text-danger">{errors.customerStoreId.message}</small>
                  )}
                </div>

                <div className="mb-3 col-md-4">
                  <label className="form-label">Invoice No</label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("invoiceNo")}
                    readOnly
                  />
                </div>

                <div className="mb-3 col-md-4">
                  <label className="form-label">Transporter</label>
                  <select
                    className="form-select"
                    {...register("transporterId")}
                  >
                    <option value="">Select Transporter</option>
                    {transporters.map((t) => (
                      <option
                        key={t.id ?? t.transporterId}
                        value={t.id ?? t.transporterId}
                      >
                        {t.transporterName || t.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-4">
                  <label className="form-label">Vehicle No</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Vehicle No (TS09AB1234)"
                    {...register("vehicleNo", {
                      onChange: (e) => {
                        e.target.value = e.target.value.toUpperCase();
                      }
                    })}
                  />
                </div>

                <div className="mb-3 col-md-4">
                  <label className="form-label">Sale Date<span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className="form-control"
                    {...register("saleDate", {
                      required: "Sale Date is required",
                      validate: (value) => {
                        if (!value) return true;
                        const selected = value;
                        const today = new Date().toLocaleDateString("en-CA");
                        if (selected > today) {
                          return "Sale Date cannot be a future date";
                        }
                        const emp = StorageService.loginData();
                        const isPrivileged =
                          emp?.roleName === "Super Admin" ||
                          emp?.roleName === "SuperAdmin" ||
                          emp?.roleName === "Data Admin" ||
                          emp?.roleName === "DataAdmin" ||
                          Number(emp?.roleId) === 1 ||
                          Number(emp?.roleId) === 2;

                        if (!isPrivileged && selected < today) {
                          return "Only Super Admin and Data Admin can select a past date";
                        }
                        return true;
                      },
                    })}
                  />
                  {errors.saleDate && (
                    <small className="text-danger">{errors.saleDate.message}</small>
                  )}
                </div>

                <div className="mb-3 col-md-4">
                  <label className="form-label">Invoice Date<span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className="form-control"
                    {...register("invoiceDate", {
                      required: "Invoice Date is required",
                      validate: (value) => {
                        if (!value) return true;
                        const selected = value;
                        const today = new Date().toLocaleDateString("en-CA");
                        if (selected > today) {
                          return "Invoice Date cannot be a future date";
                        }
                        return true;
                      },
                    })}
                  />
                  {errors.invoiceDate && (
                    <small className="text-danger">{errors.invoiceDate.message}</small>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Notes</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter notes"
                    {...register("stockSalesNotes")}
                  />
                </div>
              </div>

              <hr />
              <h5>Stock Sales Details</h5>

              <div className="table-responsive mt-2" style={{ overflowX: "auto", ...(stockItems.length >= 4 ? { maxHeight: "280px", overflowY: "auto" } : {}) }}>
                <table className="table table-bordered mb-0" style={{ minWidth: "1250px" }}>
                  <thead className="table-light sticky-top" style={{ zIndex: 2 }}>
                    <tr>
                      <th style={{ minWidth: "290px" }}>Item Category</th>
                      <th style={{ minWidth: "370px" }}>Item</th>
                      <th style={{ minWidth: "280px" }}>Item Description</th>
                      <th style={{ minWidth: "100px" }}>UOM</th>
                      <th style={{ minWidth: "130px" }}>Quantity</th>
                      <th style={{ minWidth: "80px" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockItems.map((row, idx) => (
                      <tr key={idx}>
                        <td>
                          <Select
                            value={
                              categories
                                .map((c) => ({
                                  value: c.itemCategoryId,
                                  label: c.itemCategoryName,
                                }))
                                .find((opt) => String(opt.value) === String(row.itemCategoryId)) || null
                            }
                            onChange={(selected) =>
                              handleCategoryChange(idx, selected ? selected.value : "")
                            }
                            options={getFilteredCategories().map((c) => ({
                              value: c.itemCategoryId,
                              label: c.itemCategoryName,
                            }))}
                            placeholder="Search Category..."
                            isClearable
                            menuPlacement="auto"
                            maxMenuHeight={150}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                          />
                        </td>
                        <td>
                          <Select
                            value={
                              getItemOptions(row.itemCategoryId).find(
                                (o) => String(o.value) === String(row.itemId)
                              ) || null
                            }
                            onChange={(selected) =>
                              handleChange(idx, "itemId", selected ? selected.value : "")
                            }
                            options={getItemOptions(row.itemCategoryId)}
                            placeholder="Search Item..."
                            isDisabled={!row.itemCategoryId}
                            isClearable
                            menuPlacement="auto"
                            maxMenuHeight={150}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            components={{ DropdownIndicator: null, IndicatorSeparator: null }}
                          />
                        </td>
                        <td>
                          <input
                            className="form-control fw-bold text-dark"
                            value={row.itemDescription}
                            readOnly
                          />
                        </td>
                        <td>
                          <input
                            className="form-control fw-bold text-dark"
                            value={row.uomId}
                            readOnly
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control fw-bold text-dark text-end"
                            value={row.quantity ?? ""}
                            onChange={(e) => handleQuantityChange(idx, e)}
                            onBlur={() => handleQuantityBlur(idx)}
                            placeholder="Quantity"
                          />
                        </td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="btn btn-success btn-sm me-2"
                            onClick={handleAddRow}
                          >
                            +
                          </button>
                          {stockItems.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDeleteRow(idx)}
                            >
                              -
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-3">
                <button
                  type="submit"
                  className="btn btn-primary me-2"
                  disabled={loading}
                >
                  <i className="ti ti-circle-check me-1"></i>
                  {loading
                    ? isEdit
                      ? "Updating..."
                      : "Saving..."
                    : isEdit
                      ? "Update"
                      : "Save"}
                </button>

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate("/cpnl/stocksales/view")}
                >
                  <i className="ti ti-cancel me-1"></i>
                  Cancel
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </>
  );
}
